import React, { useState, useEffect, useMemo } from 'react';
import {
  Avatar,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
} from '@mui/material';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { endLoading, startLoading } from '../../../Redux/loadingSlice';
import { usePriceFormatter } from '../../../hooks';
import { UrlConstant } from '../../../commom/UrlConstant';
import Axios from '../../../config/axios';
import CloseIcon from "@mui/icons-material/Close";
import RepaymentCard from '../Cards/repayment';
import { renderPaymentIcon } from '../../../utils/utils';
import { usePaymentMethods } from '../../../hooks/usePaymentMethods';
import BoosterLoanPaymentDialog from '../Dialogs/BoosterLoanPaymentDialog';
import Successpopup from '../SharedComponent/Successpopup';
import { CommonDataTable } from '../SharedComponent/CommonDataTable';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';

interface ActiveLoansProps {
  isrepayment: String;
  boosterLoanRepaymentData: any;
  setIsUpdatePaymentMethod: any;
}

const Repayment: React.FC<ActiveLoansProps> = ({ isrepayment, boosterLoanRepaymentData, setIsUpdatePaymentMethod }) => {
  const navigation = useNavigate();
  const dispatch = useAppDispatch();
  const formatter = usePriceFormatter();
  const currencyInfo = useAppSelector((state) => state.currency);

  const { paymentMethods, fetchAllPaymentMethods } = usePaymentMethods();
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [selectedLoanDetails, setSelectedLoanDetails] = useState<any>(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [showPaymentMethodDialog, setShowPaymentMethodDialog] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [canAddBank, setCanAddBank] = useState(false);

  // Polling state
  const [showPopup, setShowPopup] = useState(false);
  const [heading, setHeading] = useState('');
  const [Success, setSuccess] = useState(false);
  const [msg, setMsg] = useState('');
  const [isPollingPaymentStatus, setIsPollingPaymentStatus] = useState(false);
  const [trackedInstallmentIds, setTrackedInstallmentIds] = useState<string[]>([]);
  const [trackedInstallmentNos, setTrackedInstallmentNos] = useState<number[]>([]);
  const [trackedInitialStatuses, setTrackedInitialStatuses] = useState<Record<string, string>>({});
  const pollingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const pollingStartTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Debug: Log state changes
  useEffect(() => {
    console.log('State update - payDialogOpen:', payDialogOpen, 'selectedLoanDetails:', !!selectedLoanDetails);
  }, [payDialogOpen, selectedLoanDetails]);

  const normalizeStatus = (status: any): string => {
    if (!status) return '';
    return status
      .toString()
      .toUpperCase()
      .replace(/[_\s-]/g, '');
  };

  useEffect(() => {
    const getStatus = async () => {
      try {
        const response = await Axios.get(
          UrlConstant.Sumsub.CheckAccountStatus
        );
        const resData = response.data.data;
        setCanAddBank(resData.isLinkBankAvailable);
        console.log('Account status response:', resData);
      } catch (error) {
        console.error('Error getting Status', error);
      }
    };

    getStatus();
  }, []);

  const stopPolling = () => {
    if (pollingStartTimeoutRef.current) {
      clearTimeout(pollingStartTimeoutRef.current);
      pollingStartTimeoutRef.current = null;
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const getBoosterInstallments = async (loanId: string, silent: boolean = false) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const response = await Axios.get(
        UrlConstant.BoosterLoan.GetBoosterLoanInstallment.replace("{0}", loanId)
      );
      const resData = response.data;
      if (resData.status === true) {
        setSelectedLoanDetails(resData.data);
        return resData.data;
      }
      return null;
    } catch (error: any) {
      console.error("ERRR : ", error);
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // Polling logic for payment status
  const startPaymentStatusPolling = (
    installmentIds: string[],
    installmentNos?: number[],
    loanId?: string,
    successMessage: string = 'Payment processed successfully!'
  ) => {
    if (installmentIds.length === 0) {
      setHeading('Success');
      setSuccess(true);
      setMsg(successMessage);
      setShowPopup(true);
      return;
    }

    if (!loanId) {
      return;
    }

    // Prevent multiple polling sessions - if already polling, don't start another
    if (isPollingPaymentStatus || pollingIntervalRef.current) {
      console.warn('Polling already in progress, skipping new polling session');
      return;
    }

    // Ensure any existing polling is stopped
    stopPolling();

    setTrackedInstallmentIds(installmentIds);
    if (installmentNos && installmentNos.length > 0) {
      setTrackedInstallmentNos(installmentNos);
    } else {
      setTrackedInstallmentNos([]);
    }

    const initialStatusMap: Record<string, string> = {};
    selectedLoanDetails?.installments?.forEach((inst: any) => {
      if (installmentIds.includes(inst.id)) {
        initialStatusMap[inst.id] = normalizeStatus(inst.status);
      }
    });
    setTrackedInitialStatuses(initialStatusMap);
    setIsPollingPaymentStatus(true);
    setPayDialogOpen(false); // Close payment dialog

    let hasSeenInProgress = false;
    let pollCount = 0;
    let noStatusChangeCount = 0;
    const maxPolls = 60; // Maximum ~2 minutes
    const maxNoStatusChangePolls = 10; // If status doesn't change after 10 polls (10 seconds), show failure
    const trackedNos = installmentNos || []; // Capture installmentNos in closure

    const startPolling = () => {
      // Double-check we're not already polling
      if (pollingIntervalRef.current) {
        return;
      }

      pollingIntervalRef.current = setInterval(async () => {
        // Safety check: if polling interval was cleared, exit immediately
        if (!pollingIntervalRef.current) {
          return;
        }

        pollCount++;

        // Safety: stop polling after max attempts
        if (pollCount > maxPolls) {
          stopPolling();
          setIsPollingPaymentStatus(false);
          setTrackedInstallmentIds([]);
          setTrackedInstallmentNos([]);
          setTrackedInitialStatuses({});
          setHeading('Error');
          setSuccess(false);
          setMsg('Payment status check timed out. Please refresh the page.');
          setShowPopup(true);
          await getBoosterInstallments(loanId, true); // Silent refresh
          return;
        }

        const data = await getBoosterInstallments(loanId, true); // Silent fetch
        if (!data?.installments) {
          // If no data and we've been polling for a while, stop
          if (pollCount > 10) {
            stopPolling();
            setIsPollingPaymentStatus(false);
            setTrackedInstallmentIds([]);
            setTrackedInstallmentNos([]);
            setTrackedInitialStatuses({});
          }
          return;
        }

        // If tracking by installment numbers, filter by those numbers
        let trackedInstallments;
        if (trackedNos.length > 0) {
          trackedInstallments = data.installments.filter((inst: any) =>
            trackedNos.includes(inst.installment_no)
          );
        } else {
          trackedInstallments = data.installments.filter((inst: any) =>
            installmentIds.includes(inst.id)
          );
        }

        if (trackedInstallments.length === 0) {
          // If tracked installments not found after several polls, stop
          if (pollCount > 10) {
            stopPolling();
            setIsPollingPaymentStatus(false);
            setTrackedInstallmentIds([]);
            setTrackedInstallmentNos([]);
            setTrackedInitialStatuses({});
          }
          return;
        }

        const statuses = trackedInstallments.map((inst: any) => normalizeStatus(inst.status));
        const hasInProgress = statuses.includes('INPROGRESS');

        // Once we see InProgress, mark it and continue polling
        if (hasInProgress) {
          hasSeenInProgress = true;
          noStatusChangeCount = 0; // Reset counter when we see progress
        }

        const allPaid = statuses.every((status) => status === 'PAID');
        const allProcessed = statuses.every((status) => status !== 'INPROGRESS');
        const hasFailureStatus = statuses.some((status) =>
          ['FAILED', 'ERROR', 'DECLINED', 'CANCELLED'].includes(status)
        );
        const statusesChanged = trackedInstallments.some((inst: any) => {
          const currentStatus = normalizeStatus(inst.status);
          const hasInitialStatus = Object.prototype.hasOwnProperty.call(trackedInitialStatuses, inst.id);
          const initialStatus = hasInitialStatus ? trackedInitialStatuses[inst.id] : currentStatus;
          return currentStatus !== initialStatus;
        });

        // If tracking by installment numbers and status hasn't changed, increment counter
        if (trackedNos.length > 0 && !statusesChanged && !hasInProgress) {
          noStatusChangeCount++;

          // If status hasn't changed after max attempts, show failure
          if (noStatusChangeCount >= maxNoStatusChangePolls) {
            stopPolling();
            setIsPollingPaymentStatus(false);
            setTrackedInstallmentIds([]);
            setTrackedInstallmentNos([]);
            setTrackedInitialStatuses({});
            setHeading('Error');
            setSuccess(false);
            setMsg('Your payment failed due to some reasons');
            setShowPopup(true);
            await getBoosterInstallments(loanId, true); // Silent refresh
            return;
          }
        } else if (statusesChanged || hasInProgress) {
          // Reset counter if status changed or we see progress
          noStatusChangeCount = 0;
        }

        // If all are paid, stop polling and show success
        if (allPaid) {
          stopPolling();
          setIsPollingPaymentStatus(false);
          setTrackedInstallmentIds([]);
          setTrackedInstallmentNos([]);
          setTrackedInitialStatuses({});
          setHeading('Success');
          setSuccess(true);
          setMsg(successMessage);
          setShowPopup(true);
          await getBoosterInstallments(loanId, true); // Silent refresh
          setIsUpdatePaymentMethod(true);
          return;
        }

        // If we've seen InProgress and status changed away from InProgress but not all paid, it failed
        if (hasSeenInProgress && allProcessed && !allPaid) {
          stopPolling();
          setIsPollingPaymentStatus(false);
          setTrackedInstallmentIds([]);
          setTrackedInstallmentNos([]);
          setTrackedInitialStatuses({});
          setHeading('Error');
          setSuccess(false);
          setMsg('Your installment payment failed');
          setShowPopup(true);
          await getBoosterInstallments(loanId, true); // Silent refresh
          setIsUpdatePaymentMethod(true);
          return;
        }

        // If failure status detected, stop immediately
        if (hasFailureStatus) {
          stopPolling();
          setIsPollingPaymentStatus(false);
          setTrackedInstallmentIds([]);
          setTrackedInstallmentNos([]);
          setTrackedInitialStatuses({});
          setHeading('Error');
          setSuccess(false);
          setMsg('Your installment payment failed');
          setShowPopup(true);
          await getBoosterInstallments(loanId, true); // Silent refresh
          setIsUpdatePaymentMethod(true);
          return;
        }

        // If statuses changed but we never saw InProgress and not all paid, it might have failed
        if (!hasSeenInProgress && allProcessed && statusesChanged && !allPaid) {
          stopPolling();
          setIsPollingPaymentStatus(false);
          setTrackedInstallmentIds([]);
          setTrackedInstallmentNos([]);
          setTrackedInitialStatuses({});
          setHeading('Error');
          setSuccess(false);
          setMsg('Your installment payment failed');
          setShowPopup(true);
          await getBoosterInstallments(loanId, true); // Silent refresh
          setIsUpdatePaymentMethod(true);
          return;
        }

        // If nothing has changed yet and we haven't seen InProgress after several attempts, stop polling
        // (Only if not tracking by installment numbers - installment number tracking has its own logic above)
        if (trackedNos.length === 0) {
          if (!hasSeenInProgress && !hasInProgress && !statusesChanged && pollCount >= 5) {
            stopPolling();
            setIsPollingPaymentStatus(false);
            setTrackedInstallmentIds([]);
            setTrackedInstallmentNos([]);
            setTrackedInitialStatuses({});
            return;
          }
        }

        // Continue polling - loader stays constant until status changes
      }, 1000); // Poll every 1 second
    };

    // Start polling immediately (no delay)
    startPolling();
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const resolvePaymentMethodId = (method: any): string | null => {
    if (!method) return null;
    const id =
      method.id ??
      method.customerPaymentProfileId ??
      method?.rawData?.customerPaymentProfileId ??
      method?.rawData?.id;
    return id !== undefined && id !== null ? String(id) : null;
  };

  const updatePaymentMethod = async (
    loanId: string,
    paymentAccountId: string,
    type: string
  ) => {
    dispatch(startLoading());

    try {
      const response = await Axios.post(
        UrlConstant.BoosterLoan.UpdatePaymentMethod, null,
        {
          params: {
            loanId,
            customerPaymentProfileId: paymentAccountId,
            paymentMethodType: type,
          },
        }
      );

      if (response?.data?.status) {
        setIsUpdatePaymentMethod(true);
        setOpenPopup(false);
      }
    } catch (error: any) {
      console.error('Error updating payment method:', error?.response || error);
    } finally {
      dispatch(endLoading());
    }
  };

  const getLogoUrl = (symbol) => {
    if (!symbol) {
      return '';
    }
    return `https://logos-api.extend.apexclearing.com/logos/${symbol.toLowerCase().trim()}.png?api_key=4RJeGywzAE&api_secret=isUE1ZzPNi5IzXbs9Qg0S01eaFUcxX09c4U88R9JBiJgJ6WxXHrXz3VtEima`;
  };

  const navigateToDetail = async (data) => {
    navigation('/boosterLoanDetails', {
      state: {
        symbol: data.symbol,
        title: data.symbol,
        loanDetails: data,
        icon: getLogoUrl(data.symbol),
        loan_id: data.loan_id,
      },
    });
  };

  const handleOpenPopup = (row) => {
    setSelectedRow(row);
    setOpenPopup(true);
  };

  const handleClosePopup = () => {
    setOpenPopup(false);
    setSelectedRow(null);
  };

  const handleChangeMethod = (method: any, loanIdOverride?: string) => {
    const { id, type } = method;
    const loanId = loanIdOverride ?? selectedRow?.loan_id;

    if (!loanId || !id || !type) {
      console.error('Missing required data for payment method change', {
        loanId,
        id,
        type,
      });
      return;
    }

    updatePaymentMethod(loanId, id, type);
  };

  const handlePayNow = async (row: any) => {
    console.log('Pay Now clicked for loan:', row.loan_id);
    setLoading(true);
    dispatch(startLoading());
    try {
      // Fetch full loan details
      const response = await Axios.get(
        UrlConstant.BoosterLoan.GetBoosterLoanInstallment.replace("{0}", row.loan_id)
      );
      console.log('Loan details response:', response.data);

      if (response.data?.status) {
        setSelectedLoanDetails(response.data.data);
        setSelectedPaymentMethodId(row.payment_method_id);
        setPayDialogOpen(true);
        console.log('Dialog should open now');
      } else {
        console.error('API returned status false:', response.data);
        setHeading('Error');
        setSuccess(false);
        setMsg('Failed to load loan details');
        setShowPopup(true);
      }
    } catch (error) {
      console.error('Error fetching loan details:', error);
      setHeading('Error');
      setSuccess(false);
      setMsg('Failed to load loan details');
      setShowPopup(true);
    } finally {
      setLoading(false);
      dispatch(endLoading());
    }
  };

  // Define columns for CommonDataTable
  const columns = useMemo(() => [
    {
      name: 'Company',
      selector: (row: any) => row.symbol || '',
      cell: (row: any) => (
        <Box display="flex" alignItems="center">
          <Avatar
            src={row.logoUrl ? row.logoUrl : getLogoUrl(row.symbol)}
            alt={row.symbol}
            style={{ marginRight: '8px', height: '36px', width: '36px' }}
          />
          <Typography style={{ fontSize: '14px', color: 'white' }}>
            {row?.stockName || row?.symbol}
          </Typography>
        </Box>
      ),
      sortable: true,
      grow: 2,
      minWidth: '200px',
    },
    {
      name: 'Current Instalment',
      selector: (row: any) => row.installment_no || 0,
      cell: (row: any) => (
        <Typography style={{ fontSize: '16px', color: 'white', textAlign: 'end' }}>
          {row.installment_no}
        </Typography>
      ),
      sortable: true,
      right: true,
      grow: 1,
      minWidth: '150px',
    },
    {
      name: 'Day & Date',
      selector: (row: any) => row.due_date || '',
      cell: (row: any) => (
        <Typography style={{ fontSize: '16px', color: 'white', textAlign: 'end' }}>
          {moment(+row.due_date).format('MMM DD, YYYY')} |{' '}
          <span style={{ color: '#4A7AF2' }}>{moment(+row.due_date).format('dddd')}</span>
        </Typography>
      ),
      sortable: true,
      right: true,
      grow: 1,
      minWidth: '220px',
    },
    {
      name: 'Amount',
      selector: (row: any) => row.amount || 0,
      cell: (row: any) => (
        <Typography style={{ fontSize: '16px', color: 'white', textAlign: 'end' }}>
          {formatter.display(row.amount, { decimals: 2 })}
        </Typography>
      ),
      sortable: true,
      right: true,
      grow: 1,
      minWidth: '120px',
    },
    {
      name: 'Payment Method',
      selector: (row: any) => row.payment_method_id || '',
      cell: (row: any) => {
        const method = paymentMethods?.find(
          (m: any) => m.id === row.payment_method_id
        );

        if (!method) {
          return (
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
              <Typography style={{ color: "white" }}>No method</Typography>
            </Box>
          );
        }

        const icon = renderPaymentIcon(method, 28);
        const details = method.type === 'shareSellDown' || method.type === 'bankAccount'
          ? method.displayName
          : `${method.displayName} ${method.displayNumber}`;

        return (
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            {icon}
            <Typography style={{ color: "white", fontSize: '14px' }}>{details}</Typography>
          </Box>
        );
      },
      sortable: false,
      center: true,
      grow: 2,
      minWidth: '220px',
    },
    {
      name: 'Actions',
      cell: (row: any) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={1}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <Button
            style={{
              backgroundColor: '#4A7AF2',
              color: 'white',
              textTransform: 'none',
              borderRadius: '50px',
              fontSize: '14px',
              padding: '5px 10px',
            }}
            startIcon={
              <svg style={{ marginLeft: '5px' }} width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.33325 1.83398H5.99992C2.66659 1.83398 1.33325 3.16732 1.33325 6.50065V10.5007C1.33325 13.834 2.66659 15.1673 5.99992 15.1673H9.99992C13.3333 15.1673 14.6666 13.834 14.6666 10.5007V9.16732" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10.6933 2.5135L5.43992 7.76683C5.23992 7.96683 5.03992 8.36017 4.99992 8.64683L4.71325 10.6535C4.60659 11.3802 5.11992 11.8868 5.84659 11.7868L7.85325 11.5002C8.13325 11.4602 8.52659 11.2602 8.73325 11.0602L13.9866 5.80683C14.8933 4.90017 15.3199 3.84684 13.9866 2.5135C12.6533 1.18017 11.5999 1.60684 10.6933 2.5135Z" stroke="white" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9.93994 3.26758C10.3866 4.86091 11.6333 6.10758 13.2333 6.56091" stroke="white" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleOpenPopup(row);
            }}
          >
            Change Method
          </Button>
          {isrepayment === 'Repayments' && (
            <Button
              className="btn-primary"
              style={{
                backgroundColor: '#4A7AF2',
                color: 'white',
                textTransform: 'none',
                borderRadius: '50px',
                fontSize: '14px',
                padding: '5px 10px',
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handlePayNow(row);
              }}
            >
              Pay Now
            </Button>
          )}
        </Box>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      center: true,
      grow: 2,
      minWidth: '280px',
    },
  ], [paymentMethods, isrepayment, formatter, currencyInfo]);

  return (
    <>
      {(loading || isPollingPaymentStatus) && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          bgcolor="rgba(0, 0, 0, 0.5)"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap={2}
          style={{
            backdropFilter: "blur(5px)",
            zIndex: 9999,
          }}
        >
          <CircularProgress color="primary" />
          {isPollingPaymentStatus && (
            <Typography variant="body2" sx={{ color: 'white', mt: 2 }}>
              Processing payment status...
            </Typography>
          )}
        </Box>
      )}
      <div style={{
        marginTop: '30px',
        color: 'white',
        background: 'transparent',
        borderRadius: '20px',
        border: '2px solid #FFFFFF1A',
        padding: '20px',
        width: '100%',
        overflow: 'hidden'
      }}>
        <CommonDataTable
          data={boosterLoanRepaymentData || []}
          totalRows={boosterLoanRepaymentData?.length || 0}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
          columns={columns}
          title="Repayment Schedule"
          pagination={false}
          rowNavigateTo={(row: any) => ({
            url: '/boosterLoanDetails',
            state: {
              symbol: row.symbol,
              title: row.symbol,
              loanDetails: row,
              icon: getLogoUrl(row.symbol),
              loan_id: row.loan_id,
            }
          })}
          customStyles={{
            table: {
              style: {
                backgroundColor: 'transparent',
                width: '100%',
              },
            },
            tableWrapper: {
              style: {
                backgroundColor: 'transparent',
                width: '100%',
              },
            },
            headCells: {
              style: {
                fontSize: "12px",
                color: "white",
                fontWeight: "600",
                backgroundColor: "transparent",
                borderBottom: "1px solid #FFFFFF1A",
                paddingBottom: "12px",
                paddingTop: "16px",
                paddingLeft: "8px",
                paddingRight: "8px",
              },
            },
            cells: {
              style: {
                color: "white",
                fontSize: '16px',
                border: "none",
                padding: "12px 8px",
                backgroundColor: "transparent",
              },
            },
            rows: {
              style: {
                borderColor: "transparent !important",
                backgroundColor: 'transparent',
                width: '98% !important',
                borderRadius: "8px",
                margin: '2px 8px',
                padding: "4px",
                transition: "all 0.3s ease",
                cursor: 'pointer',
                outline: "none !important",
                '&:hover': {
                  border: "1px solid #4A7AF2 !important",
                  borderColor: "#4A7AF2 !important",
                  backgroundColor: '#4A7AF21A !important',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
                  outline: "none !important",
                },
                '&:nth-child(even)': {
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  border: "1px solid transparent !important",
                  borderColor: "transparent !important",
                },
                '&:nth-child(odd)': {
                  backgroundColor: 'transparent',
                  border: "1px solid transparent !important",
                  borderColor: "transparent !important",
                },
                '&:nth-child(even):hover': {
                  border: "1px solid #4A7AF2 !important",
                  borderColor: "#4A7AF2 !important",
                  backgroundColor: '#4A7AF21A !important',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
                },
                '&:nth-child(odd):hover': {
                  border: "1px solid #4A7AF2 !important",
                  borderColor: "#4A7AF2 !important",
                  backgroundColor: '#4A7AF21A !important',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
                },
              },
            },
          }}
        />
      </div>

      {/* Popup Dialog */}
      <Dialog
        open={openPopup}
        onClose={handleClosePopup}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className='text-white' sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Change Payment Method
          <IconButton onClick={handleClosePopup} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <RepaymentCard
            canAddBank={canAddBank}
            paymentMethods={paymentMethods}
            setSelectedPaymentMethod={(method: any) => handleChangeMethod(method)}
            fetchPaymentMethods={fetchAllPaymentMethods}
          />
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      {console.log('Rendering dialog - open:', payDialogOpen, 'hasDetails:', !!selectedLoanDetails)}
      <BoosterLoanPaymentDialog
        open={payDialogOpen}
        onClose={() => {
          console.log('Dialog closing');
          setPayDialogOpen(false);
          setSelectedLoanDetails(null);
        }}
        boosterLoanDetails={selectedLoanDetails}
        paymentMethods={paymentMethods}
        selectedPaymentMethodId={selectedPaymentMethodId}
        onPaymentMethodChange={(methodId) => setSelectedPaymentMethodId(methodId)}
        onPaymentSuccess={(message, installmentIds, installmentNos) => {
          const loanId = selectedLoanDetails?.loanDetail?.id || selectedRow?.loan_id;
          setShowPopup(false);
          startPaymentStatusPolling(installmentIds, installmentNos, loanId, message);
        }}
        onPaymentError={(message) => {
          setHeading('Error');
          setSuccess(false);
          setMsg(message);
          setShowPopup(true);
        }}
        onRefresh={() => {
          setIsUpdatePaymentMethod(true);
        }}
        setShowPaymentMethodDialog={setShowPaymentMethodDialog}
      />

      {/* Payment Method Dialog for BoosterLoanPaymentDialog */}
      <Dialog
        open={showPaymentMethodDialog}
        onClose={() => setShowPaymentMethodDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className='text-white' sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Change Payment Method
          <IconButton onClick={() => setShowPaymentMethodDialog(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <RepaymentCard
            paymentMethods={paymentMethods}
            setSelectedPaymentMethod={(method: any) => {
              const resolvedId = resolvePaymentMethodId(method);
              if (resolvedId) {
                setSelectedPaymentMethodId(resolvedId);
              } else {
                console.warn('Unable to resolve payment method identifier', method);
              }
              handleChangeMethod(method, selectedLoanDetails?.loanDetail?.id);
              setShowPaymentMethodDialog(false);
            }}
            fetchPaymentMethods={fetchAllPaymentMethods}
          />
        </DialogContent>
      </Dialog>

      {/* Success/Error Popup */}
      {showPopup && <Successpopup Heading={heading} Success={Success} onClose={closePopup} Message={msg} />}
    </>
  );
};

export default Repayment;
